import React from "react";
import { brand } from "../../theme/brand";

const btn: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  color: "white",
  fontSize: "0.88rem",
  padding: "0.58rem 0.9rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  cursor: "pointer",
  fontWeight: 700,
};

const subsectionStyle: React.CSSProperties = {
  border: "1px solid rgba(148,163,184,0.14)",
  borderRadius: 12,
  padding: "1rem 1.1rem",
  display: "grid",
  gap: "0.75rem",
  background: "rgba(255,255,255,0.015)",
  width: "100%",
  boxSizing: "border-box",
};

const subTitle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "0.93rem",
  color: "rgba(255,255,255,0.88)",
};

const subHelper: React.CSSProperties = {
  color: "rgba(255,255,255,0.5)",
  fontSize: "0.83rem",
  lineHeight: 1.45,
  margin: 0,
};

const subNote: React.CSSProperties = {
  color: "rgba(255,255,255,0.35)",
  fontSize: "0.78rem",
  lineHeight: 1.4,
  margin: 0,
};

type Props = {
  todayYmd: string;
  entriesAsc: any[];
  openCsvImportModal: () => void;
  downloadTextFile: (filename: string, data: string) => void;
  entriesToCsv: (entries: any[]) => string;
  exportProfileJson: () => void;
  importProfileFromSettings: (file: File) => void;
  isDemoMode: boolean;
  onNavigateToLogbook?: () => void;
};

export function DataPrivacySection({
  todayYmd,
  entriesAsc,
  openCsvImportModal,
  downloadTextFile,
  entriesToCsv,
  exportProfileJson,
  importProfileFromSettings,
  isDemoMode,
  onNavigateToLogbook,
}: Props) {
  const disableWrites = isDemoMode;
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div style={{ display: "grid", gap: "1.5rem", width: "100%", maxWidth: 560 }}>
      {/* Logbook */}
      {onNavigateToLogbook && (
        <div style={subsectionStyle}>
          <div style={subTitle}>Logbook</div>
          <button style={btn} onClick={onNavigateToLogbook}>
            Open Logbook
          </button>
        </div>
      )}

      {/* Entries CSV */}
      <div style={subsectionStyle}>
        <div style={subTitle}>Entries (CSV)</div>
        <p style={subHelper}>Import or export daily entries.</p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, max-content))", gap: "0.5rem" }}>
          <button
            style={btn}
            onClick={openCsvImportModal}
            disabled={disableWrites}
            title={disableWrites ? "Disabled in Demo" : ""}
          >
            Import CSV
          </button>
          <button
            style={btn}
            onClick={() => downloadTextFile(`weight-tracker-all-data-${todayYmd}.csv`, entriesToCsv(entriesAsc))}
            disabled={disableWrites}
            title={disableWrites ? "Disabled in Demo" : ""}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Full backup */}
      <div style={subsectionStyle}>
        <div style={subTitle}>Full Backup (JSON)</div>
        <p style={subHelper}>Backup or restore all data.</p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, max-content))", gap: "0.5rem" }}>
          <button
            style={btn}
            onClick={exportProfileJson}
            disabled={disableWrites}
            title={disableWrites ? "Disabled in Demo" : ""}
          >
            Export Backup
          </button>
          <label
            style={{
              ...btn,
              background: disableWrites ? "rgba(255,255,255,0.05)" : "rgba(59,130,246,0.12)",
              borderColor: "rgba(59,130,246,0.28)",
              color: "#bfdbfe",
              cursor: disableWrites ? "not-allowed" : "pointer",
              opacity: disableWrites ? 0.6 : 1,
            }}
            title={disableWrites ? "Disabled in Demo" : ""}
          >
            Restore Backup
            <input
              type="file"
              accept=".json,application/json,text/json"
              style={{ display: "none" }}
              disabled={disableWrites}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) importProfileFromSettings(f);
              }}
            />
          </label>
        </div>
        <p style={{ ...subNote, color: brand.state.warning, fontWeight: 500 }}>
          Restoring replaces your current profile.
        </p>
      </div>
    </div>
  );
}
